import { CallEndRounded, CallRounded, CloseFullscreenRounded, MicOffRounded, MicRounded, OpenInFullRounded, VideocamOffRounded, VideocamRounded, VolumeUpRounded } from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useEffect, useRef } from 'react';
import { chipColor } from '../../theme';
import './call.css';

// The video call takes no space of its own in the side panel, which already
// has to fit the player's hand on screen:
//   CallControls — in the player's own card: Join, or mic / camera / leave.
//   CallDock     — once in the call: everyone's picture in place of the top
//                  two cards of the side panel (who you are, and Players),
//                  this player's own small in its bottom-right corner.
//   CallThumb    — one person's picture.
//   CallStrip    — everyone's picture and the controls together, for the
//                  end-of-game and rematch dialogs that cover the panel.
//   IncomingCall — the ring, when someone else starts the call.
//   CallStage    — the call enlarged over the whole table, to just talk.
// All are driven by the one useCall() in the Board.

const STATE_LABEL = {
    connecting: 'Connecting…',
    reconnecting: 'Reconnecting…',
    failed: "Couldn't connect"
};

export const isInCall = (call) => call.status === 'in-call' || call.status === 'reconnecting';

/**
 * One person's picture. Their audio plays through the same element, so it
 * stays mounted (just covered) while their camera is off.
 */
export const CallThumb = ({ stream, name, team, self, audio, video, state, onBlocked, silent }) => {
    const ref = useRef(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        if (element.srcObject !== stream) {
            element.srcObject = stream || null;
        }
        if (stream && !self && !silent) {
            // Browsers may refuse to start sound without a click first. Any
            // other failure (an AbortError when the stream is swapped for one
            // with a new track) sorts itself out on the next play().
            element.play().catch((error) => {
                if (error?.name === 'NotAllowedError') {
                    onBlocked?.();
                }
            });
        }
    }, [stream, self, silent, onBlocked]);

    const showPicture = video && stream?.getVideoTracks().length > 0;
    const label = self ? 'Your camera' : `${name}'s camera`;
    return (
        <span className={`call-thumb${self ? ' self' : ''}`} style={{ '--c': chipColor(team) || '#8A958F' }} title={label}>
            {/* `silent`: a second picture of someone already playing their
                sound elsewhere on the page, which must not play it twice. */}
            <video ref={ref} autoPlay playsInline muted={self || silent} className={showPicture ? '' : 'hidden'} aria-label={label} />
            {!showPicture && <span className="call-avatar" aria-hidden="true">{(name || '?').charAt(0).toUpperCase()}</span>}
            {!self && state && state !== 'connected' && <span className={`call-state ${state}`}>{STATE_LABEL[state] || ''}</span>}
            {!audio && <span className="call-muted" aria-label="Muted"><MicOffRounded fontSize="inherit" /></span>}
        </span>
    );
};

/** Join, or the controls once in; plus a line of status underneath. */
export const CallControls = ({ call, me, soundBlocked, onUnblockSound, onEnlarge }) => {
    if (!call.available && call.status === 'idle') {
        return null;
    }
    const others = call.participants.filter(p => p.userId !== me?.userId);

    if (isInCall(call)) {
        let hint = null;
        if (call.status === 'reconnecting') {
            hint = 'Connection lost, getting you back in…';
        } else if (call.notice) {
            hint = call.notice;
        } else if (call.ringingOthers) {
            hint = <span className="call-ringing-out"><CallRounded fontSize="inherit" aria-hidden="true" />Ringing everyone<span className="dots" aria-hidden="true" /></span>;
        }
        return (
            <>
                <div className="call-controls" aria-label="Call controls">
                    <Tooltip title={call.audioOn ? 'Mute' : 'Unmute'}>
                        <span>
                            <IconButton size="small" aria-label={call.audioOn ? 'Mute' : 'Unmute'} className={call.audioOn ? '' : 'off'}
                                disabled={!call.hasAudio} onClick={call.toggleAudio}>
                                {call.audioOn ? <MicRounded fontSize="small" /> : <MicOffRounded fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={call.videoOn ? 'Turn camera off' : 'Turn camera on'}>
                        <IconButton size="small" aria-label={call.videoOn ? 'Turn camera off' : 'Turn camera on'} className={call.videoOn ? '' : 'off'}
                            onClick={call.toggleVideo}>
                            {call.videoOn ? <VideocamRounded fontSize="small" /> : <VideocamOffRounded fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Leave call">
                        <IconButton size="small" aria-label="Leave call" className="leave" onClick={call.leave}>
                            <CallEndRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {onEnlarge && <Tooltip title="Enlarge the call">
                        <IconButton size="small" aria-label="Enlarge the call" className="enlarge" onClick={onEnlarge}>
                            <OpenInFullRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>}
                </div>
                {soundBlocked && <Button size="small" startIcon={<VolumeUpRounded />} className="call-sound" onClick={onUnblockSound}>
                    Turn on sound
                </Button>}
                {hint && <p className="call-hint" role="status">{hint}</p>}
            </>
        );
    }

    const full = call.participants.length >= call.max;
    const joining = call.status === 'joining';
    let hint = null;
    if (call.askingPermission) {
        hint = 'Allow your camera and microphone when your browser asks.';
    } else if (call.error || call.notice) {
        hint = call.error || call.notice;
    } else if (others.length > 0) {
        hint = `${others.map(p => p.name).join(', ')} ${others.length === 1 ? 'is' : 'are'} on the call.`;
    }

    return (
        <>
            <Button
                variant="contained"
                color="secondary"
                size="small"
                className={`call-join${joining ? ' busy' : ''}`}
                startIcon={joining ? <CircularProgress size={14} color="inherit" /> : <VideocamRounded />}
                disabled={full || joining || !call.subscribed}
                onClick={call.join}
            >
                {full ? 'Call is full' : joining ? (call.askingPermission ? 'Allow camera…' : 'Joining…') : others.length > 0 ? 'Join call' : 'Start call'}
            </Button>
            {hint && <p className={`call-hint${call.error ? ' error' : ''}`} role="status">{hint}</p>}
        </>
    );
};


/**
 * Everyone on the call with the controls, for a dialog that covers the side
 * panel (the end of a game, a rematch) — so the call can be seen and run from
 * there instead of carrying on unseen behind it. Sound keeps playing from the
 * panel's pictures underneath; these are silent copies.
 */
export const CallStrip = ({ call, players, me, soundBlocked, onUnblockSound }) => {
    if (!call.available && call.status === 'idle') {
        return null;
    }
    const inCall = isInCall(call);
    const others = inCall
        ? call.participants.filter(p => p.userId !== me?.userId).map(p => ({
            participant: p,
            player: (players || []).find(player => player.userId === p.userId),
            peer: call.peers.find(peer => peer.userId === p.userId)
        }))
        : [];
    return (
        <section className="call-strip" aria-label="Video call">
            {inCall && <div className="call-strip-tiles">
                <figure className="call-strip-tile">
                    <CallThumb self silent stream={call.localStream} name={me?.name} team={me?.team} audio={call.audioOn} video={call.videoOn} />
                    <figcaption>You</figcaption>
                </figure>
                {others.map(({ participant, player, peer }) => (
                    <figure className="call-strip-tile" key={participant.sessionId}>
                        <CallThumb
                            silent
                            stream={peer?.stream}
                            state={peer?.state || 'connecting'}
                            name={player?.name || participant.name}
                            team={player?.team}
                            audio={participant.audio}
                            video={participant.video}
                        />
                        <figcaption>{player?.name || participant.name}</figcaption>
                    </figure>
                ))}
            </div>}
            <div className="call-strip-controls">
                <CallControls call={call} me={me} soundBlocked={soundBlocked} onUnblockSound={onUnblockSound} />
            </div>
        </section>
    );
};

// Two soft notes, twice, every couple of seconds: a ring, made on the spot so
// there is no sound file to load. Browsers that won't play sound before the
// page has been clicked just stay quiet; the card still shows.
const useRingTone = (on) => {
    useEffect(() => {
        if (!on) {
            return undefined;
        }
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
            return undefined;
        }
        let context;
        try {
            context = new AudioContext();
        } catch (error) {
            return undefined;
        }
        const note = (frequency, at) => {
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            oscillator.type = 'sine';
            oscillator.frequency.value = frequency;
            gain.gain.setValueAtTime(0.0001, at);
            gain.gain.exponentialRampToValueAtTime(0.16, at + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.45);
            oscillator.connect(gain).connect(context.destination);
            oscillator.start(at);
            oscillator.stop(at + 0.5);
        };
        const ring = () => {
            if (context.state !== 'running') {
                return;
            }
            const t = context.currentTime + 0.02;
            [0, 0.5].forEach(offset => {
                note(659.25, t + offset);
                note(830.6, t + offset + 0.16);
            });
        };
        context.resume().then(ring).catch(() => {});
        const timer = setInterval(ring, 2600);
        return () => {
            clearInterval(timer);
            context.close().catch(() => {});
        };
    }, [on]);
};

/** Someone else started the call: ring, with Accept and Decline. */
export const IncomingCall = ({ call }) => {
    const incoming = call.incoming;
    useRingTone(Boolean(incoming));
    if (!incoming) {
        return null;
    }
    return (
        <div className="incoming-call" role="alertdialog" aria-live="assertive" aria-label={`${incoming.name} is calling`}>
            <span className="incoming-avatar" aria-hidden="true">
                <span className="ripple" />
                <span className="ripple late" />
                {(incoming.name || '?').charAt(0).toUpperCase()}
            </span>
            <div className="incoming-text">
                <strong>{incoming.name} is calling</strong>
                <span>Video call with your table</span>
            </div>
            <div className="incoming-actions">
                <Tooltip title="Decline">
                    <IconButton aria-label="Decline" className="decline" onClick={call.decline}>
                        <CallEndRounded />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Join the call">
                    <IconButton aria-label="Accept" className="accept" onClick={call.join}>
                        <VideocamRounded />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    );
};

const DOCK_LAYOUT = { 0: 'none', 1: 'one', 2: 'two', 3: 'three', 4: 'four' };

/**
 * The call, where the top two cards of the side panel were: the others'
 * pictures filling it, this player's own small in the bottom-right corner.
 * What those cards said is still here — the score in `head`, whose turn it
 * is as a brass frame and tag on that person's picture, and anyone who is
 * not on the call as a row of names underneath. These pictures are the ones
 * that play the call's sound.
 *
 * `players` are the game's seats; `turnId` is whose turn it is (if a game is
 * on) and `turnTag` what to say on them ("Playing", the clock, "Thinking…").
 */
export const CallDock = ({ call, players, me, turnId, turnTag, head, soundBlocked, onUnblockSound, onBlocked, onEnlarge }) => {
    const seat = (userId) => (players || []).find(player => player.userId === userId);
    const others = call.participants.filter(p => p.userId !== me?.userId).map(p => ({
        participant: p,
        player: seat(p.userId),
        peer: call.peers.find(peer => peer.userId === p.userId)
    }));
    const onCall = new Set(call.participants.map(p => p.userId));
    const absent = (players || []).filter(player => player.userId !== me?.userId && !onCall.has(player.userId));
    const myTurn = Boolean(turnId) && turnId === me?.userId;

    return (
        <section className="panel-section call-dock" aria-label="Video call">
            {head}
            <div className={`call-dock-grid ${DOCK_LAYOUT[Math.min(others.length, 4)]}`}>
                {others.length === 0 && <div className="call-dock-alone">
                    {call.ringingOthers
                        ? <span className="call-ringing-out"><CallRounded fontSize="inherit" aria-hidden="true" />Ringing everyone<span className="dots" aria-hidden="true" /></span>
                        : 'Waiting for the others to join'}
                </div>}
                {others.map(({ participant, player, peer }) => {
                    const name = player?.name || participant.name;
                    const onTurn = Boolean(turnId) && turnId === participant.userId;
                    return (
                        <figure className={`call-dock-tile${onTurn ? ' on-turn' : ''}`} key={participant.sessionId}>
                            <CallThumb stream={peer?.stream} state={peer?.state || 'connecting'} name={name} team={player?.team}
                                audio={participant.audio} video={participant.video} onBlocked={onBlocked} />
                            <figcaption>
                                <span className="dot" aria-hidden="true" style={{ '--c': chipColor(player?.team) || '#8A958F' }} />
                                <span className="name">{name}</span>
                                {onTurn && <span className="tag">{turnTag}</span>}
                            </figcaption>
                        </figure>
                    );
                })}
                <figure className={`call-dock-self${myTurn ? ' on-turn' : ''}`}>
                    <CallThumb self stream={call.localStream} name={me?.name} team={me?.team} audio={call.audioOn} video={call.videoOn} />
                    {myTurn && <figcaption><span className="tag">Your turn</span></figcaption>}
                </figure>
            </div>

            {absent.length > 0 && <ul className="call-dock-absent" aria-label="Not on the call">
                {absent.map(player => {
                    const onTurn = Boolean(turnId) && turnId === player.userId;
                    return (
                        <li key={player.userId} className={onTurn ? 'on-turn' : ''}>
                            <span className="dot" aria-hidden="true" style={{ '--c': chipColor(player.team) || '#8A958F' }} />
                            <span className="name">{player.name}</span>
                            {onTurn && <span className="tag">{turnTag}</span>}
                        </li>
                    );
                })}
            </ul>}

            <div className="you call-dock-you">
                <span className="chip" style={{ '--c': chipColor(me?.team) || '#8A958F' }} aria-hidden="true" />
                <div className="call-dock-me">
                    <div className="you-team">{me?.name}</div>
                    <div className="you-name">{me?.team ? `Team ${me.team}` : 'No team yet'}</div>
                </div>
                <CallControls call={call} me={me} soundBlocked={soundBlocked} onUnblockSound={onUnblockSound} onEnlarge={onEnlarge} />
            </div>
        </section>
    );
};

const STAGE_LAYOUT = { 0: 'none', 1: 'one', 2: 'two', 3: 'three', 4: 'four' };

/**
 * The call over the whole table, for when the players just want to talk:
 * the others large, this player's own picture small in the corner. The
 * board stays underneath (sound keeps playing from its pictures; these are
 * silent copies), and the turn is shown at the top so nobody misses theirs.
 */
export const CallStage = ({ call, players, me, onClose, turn, soundBlocked, onUnblockSound }) => {
    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const others = call.participants.filter(p => p.userId !== me?.userId).map(p => ({
        participant: p,
        player: (players || []).find(player => player.userId === p.userId),
        peer: call.peers.find(peer => peer.userId === p.userId)
    }));

    let hint = null;
    if (call.status === 'reconnecting') {
        hint = 'Connection lost, getting you back in…';
    } else if (call.notice) {
        hint = call.notice;
    }

    // One on one, this player's picture sits in the corner of the other's;
    // with more, in the corner of the screen.
    const self = (
        <figure className="call-stage-self">
            <CallThumb self silent stream={call.localStream} name={me?.name} team={me?.team} audio={call.audioOn} video={call.videoOn} />
        </figure>
    );
    const oneOnOne = others.length === 1;

    return (
        <div className="call-stage" role="dialog" aria-modal="true" aria-label="Video call, enlarged">
            <div className="call-stage-top">
                {turn && <button type="button" className={`call-stage-turn${turn.mine ? ' mine' : ''}`} onClick={onClose}>
                    {turn.text}{turn.clock && <span className="secs">{turn.clock}</span>}
                </button>}
                <Tooltip title="Back to the game (Esc)">
                    <IconButton aria-label="Back to the game" className="call-stage-close" onClick={onClose}>
                        <CloseFullscreenRounded />
                    </IconButton>
                </Tooltip>
            </div>

            <div className={`call-stage-grid ${STAGE_LAYOUT[Math.min(others.length, 4)]}`}>
                {others.length === 0 && <div className="call-stage-alone">
                    {call.ringingOthers
                        ? <span className="call-ringing-out"><CallRounded fontSize="inherit" aria-hidden="true" />Ringing everyone<span className="dots" aria-hidden="true" /></span>
                        : 'Waiting for the others to join the call'}
                </div>}
                {others.map(({ participant, player, peer }) => {
                    const name = player?.name || participant.name;
                    return (
                        <div className="call-stage-cell" key={participant.sessionId}>
                            <figure className="call-stage-tile">
                                <CallThumb silent stream={peer?.stream} state={peer?.state || 'connecting'} name={name}
                                    team={player?.team} audio={participant.audio} video={participant.video} />
                                <figcaption>{name}</figcaption>
                                {oneOnOne && self}
                            </figure>
                        </div>
                    );
                })}
            </div>

            {!oneOnOne && <div className="call-stage-corner">{self}</div>}

            <div className="call-stage-bar">
                {hint && <p className="call-hint" role="status">{hint}</p>}
                {soundBlocked && <Button size="small" startIcon={<VolumeUpRounded />} className="call-sound" onClick={onUnblockSound}>Turn on sound</Button>}
                <div className="call-controls" aria-label="Call controls">
                    <Tooltip title={call.audioOn ? 'Mute' : 'Unmute'}>
                        <span>
                            <IconButton aria-label={call.audioOn ? 'Mute' : 'Unmute'} className={call.audioOn ? '' : 'off'}
                                disabled={!call.hasAudio} onClick={call.toggleAudio}>
                                {call.audioOn ? <MicRounded /> : <MicOffRounded />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={call.videoOn ? 'Turn camera off' : 'Turn camera on'}>
                        <IconButton aria-label={call.videoOn ? 'Turn camera off' : 'Turn camera on'} className={call.videoOn ? '' : 'off'} onClick={call.toggleVideo}>
                            {call.videoOn ? <VideocamRounded /> : <VideocamOffRounded />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Leave call">
                        <IconButton aria-label="Leave call" className="leave" onClick={call.leave}>
                            <CallEndRounded />
                        </IconButton>
                    </Tooltip>
                    <Button className="call-stage-back" startIcon={<CloseFullscreenRounded />} onClick={onClose}>Back to the game</Button>
                </div>
            </div>
        </div>
    );
};
